const DB_NAME = 'lsc-mixed-print-queue';
const DB_VERSION = 1;
const STATE_STORE = 'state';
const BLOB_STORE = 'blobs';
const STATE_KEY = 'active';

let databasePromise = null;
let stateWriteChain = Promise.resolve();
const memory = { state: null, blobs: new Map() };

const requestPromise = (request) =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed.'));
  });

const transactionDone = (transaction) =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = resolve;
    transaction.onerror = () =>
      reject(transaction.error || new Error('IndexedDB transaction failed.'));
    transaction.onabort = () =>
      reject(transaction.error || new Error('IndexedDB transaction was aborted.'));
  });

async function openQueueDatabase() {
  if (typeof indexedDB === 'undefined') return null;
  if (!databasePromise) {
    databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STATE_STORE)) {
          database.createObjectStore(STATE_STORE);
        }
        if (!database.objectStoreNames.contains(BLOB_STORE)) {
          database.createObjectStore(BLOB_STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('Print queue database failed.'));
    }).catch((error) => {
      databasePromise = null;
      throw error;
    });
  }
  return databasePromise;
}

export async function loadPrintQueueState() {
  const database = await openQueueDatabase();
  if (!database) return memory.state;
  const transaction = database.transaction(STATE_STORE, 'readonly');
  return requestPromise(transaction.objectStore(STATE_STORE).get(STATE_KEY));
}

export async function savePrintQueueState(state) {
  stateWriteChain = stateWriteChain.catch(() => {}).then(async () => {
    const database = await openQueueDatabase();
    if (!database) {
      memory.state = state;
      return;
    }
    const transaction = database.transaction(STATE_STORE, 'readwrite');
    transaction.objectStore(STATE_STORE).put(state, STATE_KEY);
    await transactionDone(transaction);
  });
  return stateWriteChain;
}

export async function putPrintQueueBlob(reference, blob, metadata = {}) {
  if (!reference || !(blob instanceof Blob)) {
    throw new Error('A valid print-queue Blob and reference are required.');
  }
  const estimate = await getPrintQueueStorageEstimate();
  if (
    estimate.quota > 0 &&
    estimate.usage + blob.size > Math.max(0, estimate.quota * 0.9)
  ) {
    throw new Error(
      'There is not enough browser storage for this PDF. Remove completed or unused queued labels first.',
    );
  }
  const record = { blob, metadata, createdAt: new Date().toISOString() };
  const database = await openQueueDatabase();
  if (!database) {
    memory.blobs.set(reference, record);
    return reference;
  }
  const transaction = database.transaction(BLOB_STORE, 'readwrite');
  transaction.objectStore(BLOB_STORE).put(record, reference);
  await transactionDone(transaction);
  return reference;
}

export async function getPrintQueueBlob(reference) {
  const database = await openQueueDatabase();
  if (!database) return memory.blobs.get(reference)?.blob || null;
  const transaction = database.transaction(BLOB_STORE, 'readonly');
  const record = await requestPromise(transaction.objectStore(BLOB_STORE).get(reference));
  return record?.blob || null;
}

export async function deletePrintQueueBlob(reference) {
  if (!reference) return;
  const database = await openQueueDatabase();
  if (!database) {
    memory.blobs.delete(reference);
    return;
  }
  const transaction = database.transaction(BLOB_STORE, 'readwrite');
  transaction.objectStore(BLOB_STORE).delete(reference);
  await transactionDone(transaction);
}

export async function clearPrintQueueStorage() {
  const database = await openQueueDatabase();
  memory.state = null;
  memory.blobs.clear();
  if (!database) return;
  const transaction = database.transaction([STATE_STORE, BLOB_STORE], 'readwrite');
  transaction.objectStore(STATE_STORE).clear();
  transaction.objectStore(BLOB_STORE).clear();
  await transactionDone(transaction);
}

export async function cleanupStaleTemporaryPrintQueueBlobs(maxAgeMs = 24 * 60 * 60 * 1000) {
  const cutoff = Date.now() - maxAgeMs;
  const database = await openQueueDatabase();
  if (!database) {
    for (const [reference, record] of memory.blobs) {
      if (
        record.metadata?.temporary &&
        Date.parse(record.createdAt || '') < cutoff
      ) {
        memory.blobs.delete(reference);
      }
    }
    return;
  }
  const readTransaction = database.transaction(BLOB_STORE, 'readonly');
  const store = readTransaction.objectStore(BLOB_STORE);
  const [keys, records] = await Promise.all([
    requestPromise(store.getAllKeys()),
    requestPromise(store.getAll()),
  ]);
  const staleKeys = keys.filter((_, index) => {
    const record = records[index];
    return (
      record?.metadata?.temporary &&
      Date.parse(record.createdAt || '') < cutoff
    );
  });
  if (!staleKeys.length) return;
  const writeTransaction = database.transaction(BLOB_STORE, 'readwrite');
  staleKeys.forEach((key) => writeTransaction.objectStore(BLOB_STORE).delete(key));
  await transactionDone(writeTransaction);
}

export async function getPrintQueueStorageEstimate() {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return { usage: 0, quota: 0, ratio: 0 };
  }
  const estimate = await navigator.storage.estimate();
  const usage = Number(estimate.usage) || 0;
  const quota = Number(estimate.quota) || 0;
  return { usage, quota, ratio: quota > 0 ? usage / quota : 0 };
}

export default {
  loadPrintQueueState,
  savePrintQueueState,
  putPrintQueueBlob,
  getPrintQueueBlob,
  deletePrintQueueBlob,
  clearPrintQueueStorage,
  getPrintQueueStorageEstimate,
  cleanupStaleTemporaryPrintQueueBlobs,
};
